import { Slide, Snackbar, SnackbarContent, Stack, Typography, keyframes } from '@mui/material'
import { PropsWithChildren, createContext, useEffect, useRef, useState } from 'react'
import { IDownloadStatusContext, ITrackedDownload } from './download-status.model'
import FileDownloadService from '../../shared/services/file-download/file-download.service'

const flashAnimation = keyframes`
  0% {
    opacity: 0.1;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.1;
  }
`

export const DownloadStatusContext = createContext<IDownloadStatusContext>({} as IDownloadStatusContext)

// Tracks a SET of file downloads concurrently (a single generate can produce several
// artefacts, e.g. the CRP's three files): each is polled until ready, auto-downloaded on
// completion, and the snackbar aggregates the statuses.
export const DownloadStatusProvider = ({ children }: PropsWithChildren) => {
  const fileDownloadService = new FileDownloadService()
  const [downloads, setDownloads] = useState<ITrackedDownload[]>([])
  // Ids that reached a terminal state; guards against a still-in-flight poll triggering a
  // second auto-download of the same file.
  const finishedIds = useRef<Set<string>>(new Set())

  const startDownloadTracking = (id: string) => {
    // finishedIds is never cleared wholesale: an in-flight poll for a finished id from an
    // earlier batch would otherwise miss the guard and download the same file twice. Only
    // the id being (re)started is released; growth is bounded by downloads per session.
    finishedIds.current.delete(id)
    setDownloads(prev => {
      // A fresh batch replaces entries that already finished (downloaded or failed).
      const kept = prev.some(d => d.status === 'PENDING' || d.status === 'IN_PROGRESS') ? prev : []
      if (kept.some(d => d.id === id)) {
        return kept.map(d => (d.id === id ? { ...d, status: 'PENDING' as const } : d))
      }
      return [...kept, { id, status: 'PENDING' as const }]
    })
  }

  const clearDownloadTracking = () => {
    setDownloads([])
  }

  const setDownloadStatus = (id: string, status: ITrackedDownload['status']) => {
    setDownloads(prev => {
      const current = prev.find(d => d.id === id)
      // Bail out on no-op polls so a PENDING tick does not re-render every consumer.
      if (!current || current.status === status) {
        return prev
      }
      return prev.map(d => (d.id === id ? { ...d, status } : d))
    })
  }

  const activeIds = downloads
    .filter(d => d.status === 'PENDING' || d.status === 'IN_PROGRESS')
    .map(d => d.id)
  const activeKey = activeIds.join(',')

  useEffect(() => {
    if (!activeKey) {
      return
    }
    const ids = activeKey.split(',')
    // Check each file's status as soon as it starts being tracked
    ids.forEach(id => checkStatus(id))
    // Then check again every 5 seconds
    const interval = setInterval(() => {
      ids.forEach(id => checkStatus(id))
    }, 5000)
    // Clean up when the active set changes (i.e. a file completed or failed)
    return () => {
      clearInterval(interval)
    }
  }, [activeKey])


  const checkStatus = (fileDownloadId: string) => {
    if (finishedIds.current.has(fileDownloadId)) {
      return
    }
    fileDownloadService.getOne(fileDownloadId)
      .then(response => {
        if (finishedIds.current.has(fileDownloadId)) {
          return
        }
        if (response.status === 'COMPLETED') {
          // Mark finished before starting the file transfer so an in-flight poll cannot
          // trigger a second download of the same file.
          finishedIds.current.add(fileDownloadId)
          setDownloadStatus(fileDownloadId, 'COMPLETED')
          fileDownloadService.downloadFile(response.id, response.filename)
            .catch(err => {
              console.log(err)
              setDownloadStatus(fileDownloadId, 'FAILED')
            })
        } else if (response.status === 'FAILED' || response.status === null) {
          finishedIds.current.add(fileDownloadId)
          setDownloadStatus(fileDownloadId, 'FAILED')
        } else {
          setDownloadStatus(fileDownloadId, response.status)
        }
      })
      .catch(err => {
        console.log(err)
        finishedIds.current.add(fileDownloadId)
        setDownloadStatus(fileDownloadId, 'FAILED')
      })
  }

  const total = downloads.length
  const activeCount = activeIds.length
  const completedCount = downloads.filter(d => d.status === 'COMPLETED').length
  const failedCount = downloads.filter(d => d.status === 'FAILED').length
  const allFinished = total > 0 && activeCount === 0
  const allSucceeded = allFinished && failedCount === 0

  const handleSnackbarClose = () => {
    if (allFinished) {
      clearDownloadTracking()
    }
  }

  const getStatusMessage = () => {
    // The snackbar's exit slide still renders content after the list empties.
    if (total === 0) {
      return ''
    }
    if (total === 1) {
      const status = downloads[0].status
      if (status === 'PENDING') {
        return 'Preparing'
      }
      if (status === 'IN_PROGRESS') {
        return 'Downloading'
      }
      if (status === 'COMPLETED') {
        return 'Done'
      }
      return 'There was an error, please try again'
    }
    if (activeCount > 0) {
      const verb = downloads.some(d => d.status === 'PENDING') ? 'Preparing' : 'Downloading'
      return activeCount === total
        ? `${verb} ${total} files`
        : `${verb} ${activeCount} of ${total} files`
    }
    if (failedCount === 0) {
      return `Done, ${total} files downloaded`
    }
    if (failedCount === total) {
      return 'There was an error, please try again'
    }
    return `${completedCount} of ${total} files downloaded, ${failedCount} failed`
  }

  const showStatus = total > 0

  return (
    <DownloadStatusContext.Provider value={{ downloads, startDownloadTracking, clearDownloadTracking }}>
      {children}
      <Snackbar
        open={showStatus}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={Slide}
        key={Slide.name}
        autoHideDuration={allSucceeded ? 5000 : undefined}
      >
        <SnackbarContent
          sx={{
            bgcolor: allSucceeded ? 'primary.dark' : 'text.primary',
            fontSize: '14px',
          }}
          message={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography sx={{ fontWeight: 600 }}>
                Download Status:
              </Typography>
              <Typography
                sx={{
                  ...(downloads.some(d => d.status === 'IN_PROGRESS') && {
                    animation: `${flashAnimation} 3s ease-in-out infinite`,
                  }),
                }}
              >
                {getStatusMessage()}
              </Typography>
            </Stack>
          }
        />
      </Snackbar>
    </DownloadStatusContext.Provider>
  )
}
